import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { StatusTag } from '../../../../../../components/tableTemplates'
import { useToastContext } from '../../../../../../contexts/toast'
import { AllowanceCharge } from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import {
  ComparisonArray,
  areItemsModified,
} from '../../../../../../utils/helper/checkConfirmationCodes'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config/context'
import {
  requestPutItemAllowanceChargesById,
  useInvoiceById,
  useInvoiceItemAllowanceChargeById,
} from '../../../../data'
import { RejectInvoiceItemAllowanceCharges } from './RejectInvoiceItemAllowanceCharges'
import { ResetInvoiceItemAllowanceCharges } from './ResetInvoiceItemAllowanceCharges'

type Props = {
  setDisplayEditItem: (params: boolean) => void
  visible: boolean
  setVisible: (params: boolean) => void
  invoiceId: string
  itemId: string
  id: string
}

type FormValues = {
  confirmationPercentage: number
  confirmationAmount: number
}
type FormFieldName = keyof FormValues

export const EditInvoiceItemAllowanceCharge: React.FC<Props> = ({
  setDisplayEditItem,
  visible,
  setVisible,
  invoiceId,
  itemId,
  id,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()

  const { data: invoice } = useInvoiceById(invoiceId)
  const { data: invoiceAllowanceCharge, isLoading } =
    useInvoiceItemAllowanceChargeById(invoiceId, itemId, id)

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const currency = invoice?.Payload.OrderCurrency

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationPercentage: invoiceAllowanceCharge?.ConfirmationPercentage!,
      confirmationAmount: invoiceAllowanceCharge?.ConfirmationAmount!,
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationPercentage',
          config: config.invoice.editAllowanceCharge.confirmationPercentage,
        },
        {
          fieldName: 'confirmationAmount',
          config: config.invoice.editAllowanceCharge.confirmationAmount,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // mutate()
        const isModified = getConfirmationCode(formik.values) === 'MODIFIED'
        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: isModified
            ? t(
                'invoice.allowanceCharges.table.edit.dialog.save.messages.success.summaryChanged',
                {
                  type:
                    invoiceAllowanceCharge?.ConfirmationAlcType === 'Surcharge'
                      ? t('common.codes.alcType.surcharge')
                      : t('common.codes.alcType.discount'),
                  description: invoiceAllowanceCharge?.ConfirmationDescription,
                },
              )
            : t(
                'invoice.allowanceCharges.table.edit.dialog.save.messages.success.summaryAccepted',
                {
                  type:
                    invoiceAllowanceCharge?.ConfirmationAlcType === 'Surcharge'
                      ? t('common.codes.alcType.surcharge')
                      : t('common.codes.alcType.discount'),
                  description: invoiceAllowanceCharge?.ConfirmationDescription,
                },
              ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // check if items are modified
  function getConfirmationCode(
    formData: FormValues,
  ): AllowanceCharge['Status'] {
    const comparisionArray: ComparisonArray = [
      {
        order: invoiceAllowanceCharge?.Percentage,
        confirmation: formData.confirmationPercentage,
      },
      {
        order: invoiceAllowanceCharge?.Amount,
        confirmation: formData.confirmationAmount,
      },
    ]

    return areItemsModified(comparisionArray) ? 'MODIFIED' : 'ACCEPTED'
  }

  // submit form
  const submitForm = (formData: FormValues) => {
    const newData: AllowanceCharge = produce(invoiceAllowanceCharge!, draft => {
      draft.ConfirmationPercentage = formData.confirmationPercentage
      draft.ConfirmationAmount = formData.confirmationAmount
      draft.Status = getConfirmationCode(formData)
    })

    // put for DespatchAdvice
    const response = requestPutItemAllowanceChargesById(
      invoiceId,
      itemId,
      id,
      newData,
    )

    // return response header
    return response
  }

  // handleClose
  const handleClose = () => {
    formik.resetForm()
    setDisplayEditItem(true)
    setVisible(false)
  }

  // ************************************************************
  // components
  // ************************************************************
  // alcType
  const alcTypeLabel = <label htmlFor="alcType" className="text-sm"></label>

  const alcTypeComponent = (
    <InputText
      id="alcType"
      className="w-full"
      value={t(
        invoiceAllowanceCharge?.AlcType === 'Surcharge'
          ? t('common.codes.alcType.surcharge')
          : t('common.codes.alcType.discount'),
      )}
      disabled
    />
  )

  const alcTypeWrapper = (
    <ConfigurableComponent
      config={config.invoice.editAllowanceCharge}
      component={alcTypeComponent}
      label={alcTypeLabel}
      isLoading={isLoading}
    />
  )

  // descriptionText
  const descriptionTextLabel = (
    <label htmlFor="descriptionText" className="text-sm"></label>
  )

  const descriptionTextComponent = (
    <InputText
      id="descriptionText"
      className="w-full"
      value={invoiceAllowanceCharge?.Description?.Text}
      disabled
    />
  )

  const descriptionTextWrapper = (
    <ConfigurableComponent
      config={config.invoice.editAllowanceCharge}
      component={descriptionTextComponent}
      label={descriptionTextLabel}
      isLoading={isLoading}
    />
  )

  // percentage
  const percentageLabel = (
    <label htmlFor="percentage" className="text-sm"></label>
  )

  const percentageComponent = (
    <InputNumber
      id="percentage"
      value={invoiceAllowanceCharge?.Percentage}
      className="w-full"
      suffix=" %"
      minFractionDigits={2}
      locale={language}
      disabled
    />
  )

  const percentageWrapper = (
    <ConfigurableComponent
      config={config.invoice.editAllowanceCharge}
      component={percentageComponent}
      label={percentageLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPercentage
  const confirmationPercentageLabel = (
    <label
      htmlFor="confirmationPercentage"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPercentage'),
      })}
    ></label>
  )

  const confirmationPercentageComponent = (
    <InputNumber
      id="confirmationPercentage"
      value={formik.values.confirmationPercentage}
      onChange={e => formik.setFieldValue('confirmationPercentage', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPercentage'),
      })}
      min={0}
      suffix=" %"
      minFractionDigits={2}
      showButtons
      locale={language}
      disabled={invoiceAllowanceCharge?.Status === 'REJECTED'}
    />
  )

  const confirmationPercentageErrorMsg = (
    <div>{getFormErrorMessage('confirmationPercentage')}</div>
  )

  const confirmationPercentageWrapper = (
    <ConfigurableComponent
      config={config.invoice.editAllowanceCharge}
      component={confirmationPercentageComponent}
      label={confirmationPercentageLabel}
      errorMsg={confirmationPercentageErrorMsg}
      isLoading={isLoading}
    />
  )

  // amount
  const amountLabel = <label htmlFor="amount" className="text-sm"></label>

  const amountComponent = (
    <InputNumber
      id="amount"
      mode="currency"
      currency={currency}
      value={invoiceAllowanceCharge?.Amount}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const amountWrapper = (
    <ConfigurableComponent
      config={config.invoice.editAllowanceCharge}
      component={amountComponent}
      label={amountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAmount
  const confirmationAmountLabel = (
    <label
      htmlFor="confirmationAmount"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationAmount'),
      })}
    ></label>
  )

  const confirmationAmountComponent = (
    <InputNumber
      id="confirmationAmount"
      mode="currency"
      showButtons
      currency={currency}
      value={formik.values.confirmationAmount}
      onChange={e => formik.setFieldValue('confirmationAmount', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationAmount'),
      })}
      min={0}
      locale={language}
      disabled={invoiceAllowanceCharge?.Status === 'REJECTED'}
    />
  )

  const confirmationAmountErrorMsg = (
    <div>{getFormErrorMessage('confirmationAmount')}</div>
  )

  const confirmationAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.editAllowanceCharge}
      component={confirmationAmountComponent}
      label={confirmationAmountLabel}
      errorMsg={confirmationAmountErrorMsg}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <div className="flex align-content-center flex-wrap">
      <span className="mr-3">
        {t('invoice.allowanceCharges.table.edit.dialog.title')}
      </span>

      <StatusTag status={invoiceAllowanceCharge?.Status} />
    </div>
  )

  // footer
  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      {invoiceAllowanceCharge?.Status !== 'REJECTED' ? (
        <>
          {/* reject */}
          <RejectInvoiceItemAllowanceCharges
            invoiceId={invoiceId}
            itemId={itemId}
            selectedItems={[invoiceAllowanceCharge!]}
          />

          {/* cancel */}
          <Button
            className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
            label={t('common.buttons.cancel')}
            icon="pi pi-times"
            onClick={handleClose}
            type="button"
          />

          {/* save */}
          <Button
            label={t('common.buttons.save')}
            className="w-full md:w-auto"
            icon={'pi pi-check'}
            type="submit"
            onClick={formik.submitForm}
            loading={isRequestRunning}
          />
        </>
      ) : (
        <>
          {/* reset */}
          <ResetInvoiceItemAllowanceCharges
            invoiceId={invoiceId}
            itemId={itemId}
            id={invoiceAllowanceCharge.Id!}
            formik={formik}
          />
        </>
      )}
    </div>
  )

  return (
    <>
      <Dialog
        header={header}
        visible={visible}
        style={{ width: '40rem' }}
        breakpoints={{ [calculateBreakpointWidth(40)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        {/* alcType */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{alcTypeWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* descriptionText */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{descriptionTextWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* percentage */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{percentageWrapper}</div>
          <div className="col-12 md:col-6">{confirmationPercentageWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* amount */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{amountWrapper}</div>
          <div className="col-12 md:col-6">{confirmationAmountWrapper}</div>
        </div>
      </Dialog>
    </>
  )
}
